/* eslint-disable consistent-return */
import { Auth } from '@aws-amplify/auth';
import { notification } from 'antd';
import history from './history';
import axios from 'axios';
import get from 'lodash/get';
import { EMAIL_NOTIFICATION_URL } from '../config';
import { shouldExcludeFromRedirect } from '../utils/routeHelpers';
import {
  isMockAuthEnabled,
  isMockSessionActive,
  validateMockCredentials,
  buildMockCognitoUser,
  persistMockSessionMarker,
  clearMockSessionMarker,
  getMockGroupsFromEnv,
  getStoredMockUsername,
} from './mockAuth';
import { clearAuditSession } from '../features/audit/auditSession';

export { isMockAuthEnabled } from './mockAuth';

/** Clear client auth state but preserve theme preference. */
const finalizeClientLogout = () => {
  const theme = sessionStorage.getItem('theme');
  localStorage.clear();
  sessionStorage.clear();
  if (theme != null) {
    sessionStorage.setItem('theme', theme);
  }
};

let user = null;

const authenticateCurrentUser = async () => {
  if (isMockAuthEnabled() && isMockSessionActive()) {
    const stored = getStoredMockUsername();
    return buildMockCognitoUser(
      stored || process.env.REACT_APP_MOCK_USERNAME || 'dev@local.test'
    );
  }
  const currentUser = await Auth.currentAuthenticatedUser();
  return currentUser;
};

const getAwsGroupAccess = async () => {
  if (isMockAuthEnabled() && isMockSessionActive()) {
    return getMockGroupsFromEnv();
  }
  const cognitoUser = await Auth.currentAuthenticatedUser();
  return cognitoUser.signInUserSession.accessToken.payload['cognito:groups'];
};

const userSignOut = async () => {
  clearAuditSession();
  if (isMockAuthEnabled() && isMockSessionActive()) {
    clearMockSessionMarker();
    localStorage.removeItem('ap_idToken');
    localStorage.removeItem('ap_rToken');
    localStorage.removeItem('user');
    user = null;
    return true;
  }
  if (isMockAuthEnabled()) {
    try {
      await Auth.signOut();
    } catch {
      /* No Cognito session (e.g. mock credential check before OTP path) */
    }
    user = null;
    return true;
  }
  try {
    const isSignedOut = await Auth.signOut();
    clearTokens();
    // Clear all auth-related localStorage
    localStorage.removeItem('ap_idToken');
    localStorage.removeItem('ap_rToken');
    localStorage.removeItem('user');
    return isSignedOut;
  } catch (error) {
    // Even if sign out fails, clear local storage
    clearTokens();
    localStorage.removeItem('ap_idToken');
    localStorage.removeItem('ap_rToken');
    localStorage.removeItem('user');
    throw error;
  }
};

const normalizeUsername = (username) => username?.toLowerCase();

const userSignIn = async (username, password) => {
  if (isMockAuthEnabled()) {
    validateMockCredentials(username, password);
    const userData = buildMockCognitoUser(normalizeUsername(username));
    user = userData;
    return userData;
  }
  const userData = await Auth.signIn(normalizeUsername(username), password);
  user = userData;
  return userData;
};

const userSignUp = async (username, password, attributes) => {
  const userData = await Auth.signUp({
    username: normalizeUsername(username),
    password,
    attributes: {
      'custom:first_name': attributes.firstName,
      'custom:last_name': attributes.lastName,
    },
  });
  return userData;
};

const getJwtToken = async () => {
  if (isMockAuthEnabled() && isMockSessionActive()) {
    const t = localStorage.getItem('ap_idToken');
    if (t) return t;
    clearMockSessionMarker();
    localStorage.removeItem('ap_idToken');
    localStorage.removeItem('ap_rToken');
    localStorage.removeItem('user');
    history.push('/login');
    return null;
  }
  try {
    const sessionBefore = await Auth.currentSession();
    if (sessionBefore) {
      localStorage.setItem('ap_idToken', sessionBefore.getIdToken().getJwtToken());
      localStorage.setItem('ap_rToken', sessionBefore.refreshToken.token);
      return sessionBefore.getIdToken().getJwtToken();
    }
  } catch (error) {
    const msg = error?.message || String(error || '');
    if (msg === 'No current user' || /not authenticated/i.test(msg)) {
      return localStorage.getItem('ap_idToken') || null;
    }
    console.debug('getJwtToken failed', error);
    return localStorage.getItem('ap_idToken') || null;
  }
  localStorage.clear();
  history.push('/login');
  return null;
};

const setTokens = async (data) => {
  try {
    const idToken = data.signInUserSession.idToken.jwtToken;
    const refreshToken = data?.signInUserSession.refreshToken.token;
    localStorage.setItem('ap_idToken', idToken);
    localStorage.setItem('ap_rToken', refreshToken);
    // setTimeout()
    const axiosConfig = axios.create({
      baseURL: process.env.REACT_APP_BASE_URL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    });
    const userAccess = await getAwsGroupAccess();

    if (data.__pihMockAuth) {
      persistMockSessionMarker(data.username);
    }

    // Check current path before redirecting - don't redirect if on excluded paths
    if (shouldExcludeFromRedirect()) {
      return; // Don't redirect if already on a technical page or modern layout route
    }

    if (
      userAccess?.includes('web-page') ||
      userAccess?.includes('web-page-without-create-project') ||
      userAccess?.includes('super-admin')
    )
      history.push('/projects');
    else if (userAccess?.includes('admin')) history.push('/admin');
    else if (
      ['heat-profile', 'coating-record-db', 'dcal-generator'].some((group) =>
        userAccess?.includes(group)
      )
    ) {
      history.push('/technical');
    } else if (userAccess?.includes('analysis')) history.push('/log-report');
    else {
      history.push('/web-page');
    }
  } catch (error) {
    console.debug('error setting token', error);
  }
};

const clearTokens = async () => {
  try {
    localStorage.clear('ap_idToken');
    localStorage.clear('ap_rToken');
    // history.push('/login');  
  } catch (error) {
    // console.debug('error setting token', error);
  }
};

export const getToken = () => {
  // getJwtToken();
  return localStorage.getItem('ap_idToken') ? localStorage.getItem('ap_idToken') : null;
};
// export const getToken = () => {getJwtToken()};

export const getAuthorizationHeader = () => `Bearer ${getToken()}`;

const forgotPassword = (email) => {
  try {
    return new Promise((resolve, reject) => {
      Auth.forgotPassword(email)
        .then((data) => {
          notification.destroy();
          notification.success({
            message: `Verification code sent to ${data.CodeDeliveryDetails.Destination}`,
          });
          resolve(data.CodeDeliveryDetails.Destination);
        })
        .catch((err) => {
          notification.destroy();
          if (err.message === 'Network error') {
            notification.error({
              message: err.message,
            });
          } else if (
            err.message ===
            'Exceeded daily email limit for the operation or the account. If a higher limit is required, please configure your user pool to use your own Amazon SES configuration for sending email.'
          ) {
            notification.error({
              message: 'Server is busy. Please try again later.',
            });
          } else {
            notification.error({
              message: 'User not found',
            });
          }
          reject(err);
        });
    });
  } catch (error) {
    notification.destroy();
    notification.error({
      message: 'Unable to fetch user',
    });
  }
};

const forgotPasswordSubmit = (email, code, newPass) =>
  new Promise((resolve, reject) => {
    Auth.forgotPasswordSubmit(email, code, newPass)
      .then(() => {
        notification.destroy();
        notification.success({
          message: 'Password changed successfully',
        });
        history.push('./login');
        resolve('');
      })
      .catch((err) => {
        notification.destroy();
        if (err.code === 'InvalidParameterException' || err.code === 'InvalidPasswordException') {
          notification.error({
            message:
              'Passwords must be at least 8 characters in length including one number, special character, uppercase and lowercase.',
          });
        } else if (err.code === 'CodeMismatchException' || err.code === 'ExpiredCodeException') {
          notification.error({
            message: 'MisMatch in verification code ',
          });
        } else if (err.code === 'UserNotFoundException') {
          notification.error({
            message: 'User not Found ',
          });
        } else if (err.message === 'Network error') {
          notification.error({
            message: err.message,
          });
        } else {
          notification.error({
            message: err.message,
          });
          reject(err);
        }
      });
  });

const resetPasswordSubmit = (newPass, fullName) =>
  new Promise((resolve, reject) => {
    Auth.completeNewPassword(user, newPass, { name: fullName })
      .then(() => {
        if (user) {
          notification.destroy();
          notification.success({
            message: 'Password changed successfully',
          });
          history.push('./login');
          resolve('');
        }
      })
      .catch((err) => {
        notification.destroy();
        if (err.code === 'InvalidParameterException' || err.code === 'InvalidPasswordException') {
          notification.error({
            message:
              'Passwords must be at least 8 characters in length including one number, special character, uppercase and lowercase.',
          });
        } else if (err.code === 'CodeMismatchException' || err.code === 'ExpiredCodeException') {
          notification.error({
            message: 'MisMatch in verification code ',
          });
        } else if (err.code === 'UserNotFoundException') {
          notification.error({
            message: 'User not Found ',
          });
        } else if (err.message === 'Network error') {
          notification.error({
            message: err.message,
          });
        } else {
          notification.error({
            message: err.message,
          });
          reject(err);
        }
      });
  });

const resendVerifyLink = (username) =>
  new Promise((resolve, reject) => {
    Auth.resendSignUp(username)
      .then((data) => {
        notification.destroy();
        notification.success({
          message: `Verification link sent to ${data.CodeDeliveryDetails.Destination}`,
        });
        resolve('');
      })
      .catch((err) => {
        notification.destroy();
        if (err.message === 'Network error') {
          notification.error({
            message: err.message,
          });
        } else {
          notification.error({
            message: 'Please try signing up again.',
          });
        }
        reject(err);
      });
  });

const changeProfilePassword = (oldPassword, newPassword) =>
  new Promise((resolve, reject) => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        return Auth.changePassword(user, oldPassword, newPassword);
      })
      .then((data) => {
        if (data) {
          notification.destroy();
          notification.success({
            message: 'Passoword Changed Successfully.',
          });
          resolve('');
        }
      })
      .catch((err) => {
        if (err.message === 'Incorrect username or password.') {
          notification.warn({
            message: 'Provided Incorrect Current Password.',
            description: 'Please try again providing correct passowrd.',
          });
        } else if (err.message === 'Attempt limit exceeded, please try after some time.') {
          notification.warn({
            message: 'Attempt limit exceeded, please try after some time.',
          });
        } else if (err.message === 'Network error') {
          notification.error({
            message: err.message,
          });
        } else {
          notification.Warn({
            message: 'Server is busy. Please try again later.',
          });
        }
      });
  });

// OTP-related functions for login flow (Data360-style)

/**
 * Check whether OTP/MFA is required for this email (skip window).
 * @param {string} email
 * @returns {Promise<Object>} Response body with data.requireOtp
 */
const getMfaStatus = async (email) => {
  try {
    const response = await axios.get(
      `${EMAIL_NOTIFICATION_URL}/pih/mfa-status?email=${encodeURIComponent(email)}`
    );
    const body = response.data;
    if (body?.success !== 1 && body?.statusCode !== 200) {
      throw body || { message: 'Failed to get MFA status' };
    }
    return body;
  } catch (error) {
    const payload = error.response?.data || error;
    console.error('Error getting MFA status:', payload);
    throw typeof payload === 'object' && payload !== null
      ? payload
      : { message: 'Failed to get MFA status' };
  }
};

/**
 * Send OTP to user's email for login
 * @param {Object} data - Object with email property
 * @returns {Promise<Object>} Response data
 */
const sendLoginOtp = async (data) => {
  try {
    const response = await axios.post(`${EMAIL_NOTIFICATION_URL}/pih/send-otp`, data);
    console.log('Login OTP sent successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending login OTP:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to send OTP' };
  }
};

/**
 * Verify OTP for login
 * @param {Object} data - Object with email, code, and optional user_id (Cognito sub)
 * @returns {Promise<Object>} Response data with success property
 */
const verifyLoginOtp = async (data) => {
  try {
    const body = { email: data.email, code: data.code };
    if (data.user_id != null && data.user_id !== '') {
      body.user_id = data.user_id;
    }
    const response = await axios.post(`${EMAIL_NOTIFICATION_URL}/pih/verify-otp`, body);
    console.log('Login OTP verified successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error verifying login OTP:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to verify OTP' };
  }
};

/**
 * Send audit notification for self-service password reset request (e.g. Forgot password).
 * Uses same base URL as reset-password-link. Best-effort only: logs errors, does not throw.
 * Do not block or change UX on failure.
 * @param {{ userEmail: string, requestedAt: string, userName?: string, requestedBy?: string }} payload
 */
const sendPasswordResetRequestNotification = async (payload) => {
  try {
    await axios.post(
      `${EMAIL_NOTIFICATION_URL}/pih/send-password-reset-request-notification`,
      {
        userEmail: payload.userEmail,
        requestedAt: payload.requestedAt,
        ...(payload.userName != null && { userName: payload.userName }),
        ...(payload.requestedBy != null && { requestedBy: payload.requestedBy }),
      }
    );
  } catch (err) {
    console.error('Password reset audit notification failed:', err.response?.data || err.message);
  }
};

export {
  authenticateCurrentUser,
  userSignOut,
  getJwtToken,
  userSignIn,
  clearTokens,
  setTokens,
  forgotPasswordSubmit,
  forgotPassword,
  resetPasswordSubmit,
  userSignUp,
  resendVerifyLink,
  changeProfilePassword,
  getAwsGroupAccess,
  getMfaStatus,
  sendLoginOtp,
  verifyLoginOtp,
  sendPasswordResetRequestNotification,
  finalizeClientLogout,
};
